import type { ReactNode, SVGProps } from "react";

type IconProps = Omit<SVGProps<SVGSVGElement>, "children"> & {
  icon: string;
  width?: number | string;
  height?: number | string;
};

type IconDefinition = {
  fill?: boolean;
  node: ReactNode;
};

const iconDefinitions: Record<string, IconDefinition> = {
  "mdi:arrow-right": {
    node: (
      <>
        <path d="M5 12h14" />
        <path d="m13 5 7 7-7 7" />
      </>
    ),
  },
  "mdi:arrow-left": {
    node: (
      <>
        <path d="M19 12H5" />
        <path d="m11 5-7 7 7 7" />
      </>
    ),
  },
  "mdi:arrow-up": {
    node: (
      <>
        <path d="M12 19V5" />
        <path d="m5 11 7-7 7 7" />
      </>
    ),
  },
  "mdi:arrow-down": {
    node: (
      <>
        <path d="M12 5v14" />
        <path d="m19 13-7 7-7-7" />
      </>
    ),
  },
  "mdi:open-in-new": {
    node: (
      <>
        <path d="M14 4h6v6" />
        <path d="m10 14 10-10" />
        <path d="M20 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h5" />
      </>
    ),
  },
  "mdi:chevron-down": {
    node: <path d="m6 9 6 6 6-6" />,
  },
  "mdi:circle": {
    fill: true,
    node: <circle cx="12" cy="12" r="6" />,
  },
  "fa7-solid:caret-right": {
    fill: true,
    node: <path d="m9 5 9 7-9 7z" />,
  },
  "mdi:check-circle": {
    node: (
      <>
        <path d="M20 6 9 17l-5-5" />
        <circle cx="12" cy="12" r="9" />
      </>
    ),
  },
  "mdi:check-circle-outline": {
    node: (
      <>
        <path d="M20 6 9 17l-5-5" />
        <circle cx="12" cy="12" r="9" />
      </>
    ),
  },
  "mdi:checkbox-marked-circle-outline": {
    node: (
      <>
        <path d="M20 6 9 17l-5-5" />
        <circle cx="12" cy="12" r="9" />
      </>
    ),
  },
  "mdi:check-decagram": {
    node: (
      <>
        <path d="M20 6 9 17l-5-5" />
        <path d="m12 2 2.2 3.1 3.8-.4.7 3.8 3.1 2.2-1.8 3.4.4 3.8-3.8.7-2.2 3.1-3.4-1.8-3.8.4-.7-3.8-3.1-2.2 1.8-3.4-.4-3.8 3.8-.7z" />
      </>
    ),
  },
  "mdi:plus-circle-outline": {
    node: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v8" />
        <path d="M8 12h8" />
      </>
    ),
  },
  "mdi:send": {
    node: (
      <>
        <path d="m22 2-7 20-4-9-9-4z" />
        <path d="M22 2 11 13" />
      </>
    ),
  },
  "mdi:clipboard-check-outline": {
    node: (
      <>
        <path d="M9 5h6" />
        <path d="M9 3h6v4H9z" />
        <path d="M8 5H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
        <path d="m8 14 2.5 2.5L16 11" />
      </>
    ),
  },
  "mdi:target-arrow": {
    node: (
      <>
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="3" />
        <path d="M15 9 21 3" />
        <path d="M18 3h3v3" />
      </>
    ),
  },
  "mdi:account-check-outline": {
    node: (
      <>
        <circle cx="9" cy="8" r="4" />
        <path d="M3 21a6 6 0 0 1 12 0" />
        <path d="m16 12 2 2 4-5" />
      </>
    ),
  },
  "mdi:account-tie-outline": {
    node: (
      <>
        <circle cx="12" cy="7" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
        <path d="m10 13 2 3 2-3" />
        <path d="m12 16-1.5 5" />
        <path d="m12 16 1.5 5" />
      </>
    ),
  },
  "mdi:timeline-clock-outline": {
    node: (
      <>
        <path d="M4 6h7" />
        <path d="M4 12h5" />
        <path d="M4 18h4" />
        <circle cx="16" cy="15" r="5" />
        <path d="M16 12v3l2 2" />
      </>
    ),
  },
  "mdi:cash-check": {
    node: (
      <>
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <circle cx="10" cy="12" r="2" />
        <path d="m14 12 2 2 4-5" />
      </>
    ),
  },
  "mdi:shield-check-outline": {
    node: (
      <>
        <path d="M12 3 5 6v5c0 5 3.5 8.5 7 10 3.5-1.5 7-5 7-10V6z" />
        <path d="m8.5 12 2.5 2.5 4.5-5" />
      </>
    ),
  },
  "mdi:map-marker-star-outline": {
    node: (
      <>
        <path d="M12 21s7-5.4 7-12a7 7 0 1 0-14 0c0 6.6 7 12 7 12z" />
        <path d="m12 7 .8 1.8 2 .2-1.5 1.3.5 2-1.8-1-1.8 1 .5-2L9.2 9l2-.2z" />
      </>
    ),
  },
  "mdi:calendar-check-outline": {
    node: (
      <>
        <rect x="4" y="5" width="16" height="16" rx="2" />
        <path d="M8 3v4" />
        <path d="M16 3v4" />
        <path d="M4 10h16" />
        <path d="m8 15 2 2 5-5" />
      </>
    ),
  },
  "mdi:calendar-check": {
    node: (
      <>
        <rect x="4" y="5" width="16" height="16" rx="2" />
        <path d="M8 3v4" />
        <path d="M16 3v4" />
        <path d="M4 10h16" />
        <path d="m8 15 2 2 5-5" />
      </>
    ),
  },
  "mdi:identifier": {
    node: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M7 9h6" />
        <path d="M7 13h10" />
        <path d="M7 17h5" />
      </>
    ),
  },
  "mdi:package-variant-closed-check": {
    node: (
      <>
        <path d="m12 3 8 4-8 4-8-4z" />
        <path d="M4 7v9l8 5 8-5V7" />
        <path d="M12 11v10" />
        <path d="m14.5 15.5 1.5 1.5 3-3" />
      </>
    ),
  },
  "mdi:web": {
    node: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18" />
        <path d="M12 3a13 13 0 0 1 0 18" />
        <path d="M12 3a13 13 0 0 0 0 18" />
      </>
    ),
  },
  "mdi:cart": {
    node: (
      <>
        <path d="M4 5h2l2 10h9l3-7H7" />
        <circle cx="10" cy="20" r="1" />
        <circle cx="17" cy="20" r="1" />
      </>
    ),
  },
  "mdi:cellphone": {
    node: (
      <>
        <rect x="7" y="2" width="10" height="20" rx="2" />
        <path d="M11 18h2" />
      </>
    ),
  },
  "mdi:shape": {
    node: (
      <>
        <rect x="4" y="4" width="7" height="7" rx="1" />
        <circle cx="16.5" cy="7.5" r="3.5" />
        <path d="m8 15 4 6 4-6z" />
      </>
    ),
  },
  "mdi:shape-plus": {
    node: (
      <>
        <rect x="4" y="4" width="7" height="7" rx="1" />
        <circle cx="16.5" cy="7.5" r="3.5" />
        <path d="m8 15 4 6 4-6z" />
        <path d="M18 16v6" />
        <path d="M15 19h6" />
      </>
    ),
  },
  "mdi:form-select": {
    node: (
      <>
        <rect x="5" y="3" width="14" height="18" rx="2" />
        <path d="M8 8h8" />
        <path d="M8 12h8" />
        <path d="M8 16h4" />
      </>
    ),
  },
  "mdi:cog-play": {
    node: (
      <>
        <circle cx="10" cy="12" r="4" />
        <path d="M10 2v3" />
        <path d="M10 19v3" />
        <path d="M2 12h3" />
        <path d="M15 12h2" />
        <path d="m17 16 5-3-5-3z" />
      </>
    ),
  },
  "mdi:rocket-launch": {
    node: (
      <>
        <path d="M5 19c1.5-5 5-10 12-14 1.5 5-1 10-6 13z" />
        <path d="M9 15 5 19" />
        <path d="M14 6h4v4" />
        <path d="M4 14l-2-2 4-2" />
      </>
    ),
  },
  "basil:facebook-solid": {
    fill: true,
    node: <path d="M14 8h3V4h-3c-3.3 0-5 2-5 5v2H6v4h3v7h4v-7h3l1-4h-4V9c0-.7.3-1 1-1z" />,
  },
  "basil:instagram-solid": {
    node: (
      <>
        <rect x="4" y="4" width="16" height="16" rx="5" />
        <circle cx="12" cy="12" r="3.5" />
        <circle cx="17" cy="7" r="0.8" fill="currentColor" stroke="none" />
      </>
    ),
  },
  "fa7-brands:x-twitter": {
    fill: true,
    node: <path d="M4 4h4.6l4.1 5.5L17.5 4H20l-6.1 7 6.5 9h-4.6l-4.5-6-5.2 6H3.5l6.6-7.6z" />,
  },
};

export function Icon({
  icon,
  width,
  height,
  className,
  ...props
}: IconProps) {
  const definition = iconDefinitions[icon] ?? iconDefinitions["mdi:shape"];
  const resolvedWidth = width ?? height ?? "1em";
  const resolvedHeight = height ?? width ?? "1em";

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      width={resolvedWidth}
      height={resolvedHeight}
      className={className}
      fill={definition.fill ? "currentColor" : "none"}
      stroke={definition.fill ? "none" : "currentColor"}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      {...props}
    >
      {definition.node}
    </svg>
  );
}

export default Icon;
